import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const BestSeller = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  // All segment attributes filtered by visibleFields
  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-best-seller">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Decorative: corner triangles ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, width: 0, height: 0,
        borderStyle: "solid", borderWidth: "80px 80px 0 0",
        borderColor: "var(--theme-accent) transparent transparent transparent",
        opacity: 0.22, zIndex: 1,
      }} />
      <div style={{
        position: "absolute", bottom: 0, right: 0, width: 0, height: 0,
        borderStyle: "solid", borderWidth: "0 0 80px 80px",
        borderColor: "transparent transparent var(--theme-accent) transparent",
        opacity: 0.22, zIndex: 1,
      }} />
      {/* ── Decorative: dot grids ── */}
      {["right:14px", "left:14px"].map((side, si) => (
        <div key={si} style={{
          position: "absolute", top: 60, bottom: 140,
          [side.split(":")[0]]: side.split(":")[1],
          display: "grid", gridTemplateColumns: "repeat(4,1fr)",
          gap: 6, width: 44, zIndex: 2, opacity: 0.3, pointerEvents: "none",
        }}>
          {Array.from({ length: 32 }).map((_, i) => (
            <div key={i} style={{
              width: 3, height: 3, borderRadius: "50%",
              background: "var(--theme-accent)",
            }} />
          ))}
        </div>
      ))}
      {/* ── Accent bars ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 5,
        background: "var(--theme-accent)", zIndex: 10,
      }} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 5,
        background: "var(--theme-accent)", zIndex: 10,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", height: "100%",
        padding: "8px 28px 12px",
      }}>

        {/* ── Brand bar ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, paddingTop: 10, marginBottom: 8, width: "100%",
        }}>
          {isVisible("logoUrl") && buyer?.logoUrl && (
            <img src={buyer.logoUrl} alt="Logo"
              style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 4 }} />
          )}
          {isVisible("organizationName") && (
            <span style={{
              fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
              fontSize: "0.75rem", fontWeight: 800,
              color: "var(--theme-text)",
              textTransform: "uppercase", letterSpacing: "0.15em",
            }}>
              {buyer?.organizationName || "YOUR BRAND"}
            </span>
          )}
        </div>

        {/* ── Best selling label ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginBottom: 10, width: "100%", justifyContent: "center",
        }}>
          <div style={{ flex: 1, height: 1, background: "var(--theme-border)" }} />
          <div style={{
            fontSize: "0.58rem", fontWeight: 800,
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: "var(--theme-accent)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            whiteSpace: "nowrap",
          }}>
            ★ BEST SELLING PRODUCT ★
          </div>
          <div style={{ flex: 1, height: 1, background: "var(--theme-border)" }} />
        </div>

        {/* ── Image frame ── */}
        <div style={{
          position: "relative", width: "64%", aspectRatio: "1 / 1.1",
          background: "var(--theme-surface)",
          border: "2px solid var(--theme-accent)",
          borderRadius: 6,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 8, marginBottom: 8, overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(circle at center,var(--theme-accent) 0%,transparent 70%)",
            opacity: 0.07, pointerEvents: "none",
          }} />
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
              position: "relative", zIndex: 1,
              filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.28))",
            }}
          />
          {/* segment badge */}
          <div style={{
            position: "absolute", top: 6, left: 6,
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.52rem", fontWeight: 800,
            padding: "2px 7px", borderRadius: 2, zIndex: 2,
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            {product?.segment?.toUpperCase() || "JEWELLERY"}
          </div>
        </div>

        {/* ── Accent bar ── */}
        <div style={{
          width: "55%", height: 12,
          background: "var(--theme-accent)",
          borderRadius: 2, marginBottom: 8,
        }} />

        {/* ── Product name ── */}
        <h2 style={{
          fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
          fontSize: "0.95rem", fontWeight: 800,
          color: "var(--theme-text)", margin: "0 0 6px",
          letterSpacing: "0.02em", lineHeight: 1.2,
        }}>
          {product?.name || "Fine Jewellery"}
        </h2>

        {/* ── All segment attributes ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 8px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)",
            borderRadius: 4, padding: "6px 10px",
            marginBottom: 8, width: "100%", textAlign: "left",
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.5rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                    <DiamondShapeSVG
                      shapeName={String(v)}
                      size={26}
                      color="var(--theme-accent)"
                    />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600,
                      color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>
                      {fmtVal(v)}
                    </span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.66rem", fontWeight: 600,
                    color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>
                    {fmtVal(v)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Promo text ── */}
        <p style={{
          fontSize: "0.64rem", lineHeight: 1.4,
          color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          margin: "0 0 6px", maxWidth: "80%",
        }}>
          {product?.description
            ? product.description.slice(0, 70) + (product.description.length > 70 ? "…" : "")
            : <>Get the best selling products today, and win{" "}
              <strong style={{ color: "var(--theme-accent)" }}>discounts up to 50% OFF</strong></>}
        </p>

        <div style={{ flex: 1 }} />

        {/* ── Price ── */}
        <div style={{
          background: "var(--theme-accent)", color: "var(--theme-bg)",
          fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
          fontSize: "1.05rem", fontWeight: 800,
          padding: "7px 28px", borderRadius: 4,
          letterSpacing: "0.02em", marginBottom: 8,
        }}>
          {formatPrice(price)}
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", width: "100%",
          borderTop: "1px solid var(--theme-border)", paddingTop: 7,
          fontSize: "0.54rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
        }}>
          {isVisible("address") && (
            <span>📍 {buyer?.streetAddress || "123 Jewellers Lane"}</span>
          )}
          {isVisible("phone") && (
            <span>📞 {buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>
          )}
        </div>
      </div>
    </div>
  );
});

BestSeller.displayName = "BestSeller";
export default BestSeller;
